import { useEffect, useState } from "react";
import { Loader2, Calculator } from "lucide-react";
import { api } from "@/lib/api";
import { mxn, fdate, formatearPlazo } from "@/lib/format";

const CeldaFactoraje = ({ e }) => (
  <>
    <p className="mt-0.5 text-sm font-semibold text-slate-800">{mxn(e.monto)} · Pago único al vencimiento</p>
    <p className="mt-1 text-lg font-semibold text-slate-900">
      {mxn(e.pago_periodo)}
      <span className="ml-1 text-[11px] font-normal text-slate-500">al vencimiento ({fdate(e.fecha_pago)})</span>
    </p>
    <dl className="mt-1 space-y-0.5 text-[11px] text-slate-600">
      <div className="flex justify-between"><dt>Días</dt><dd className="font-mono">{e.dias_descuento}</dd></div>
      <div className="flex justify-between"><dt>Descuento (interés)</dt><dd className="font-mono">{mxn(e.descuento)}</dd></div>
      <div className="flex justify-between"><dt>IVA del descuento</dt><dd className="font-mono">{mxn(e.iva_descuento)}</dd></div>
      <div className="flex justify-between"><dt>Total a pagar</dt><dd className="font-mono">{mxn(e.total_pagar)}</dd></div>
      <div className="flex justify-between"><dt>Recibe</dt><dd className="font-mono">{mxn(e.neto_a_recibir)}</dd></div>
      <div className="flex justify-between"><dt>CAT estimado</dt><dd className="font-mono">{e.cat != null ? `${e.cat}%` : "—"}</dd></div>
    </dl>
  </>
);

const Celda = ({ titulo, e, destacar, testid }) => (
  <div data-testid={testid}
    className={"rounded-md border p-2.5 " + (destacar
      ? "border-sky-300 bg-white" : "border-slate-200 bg-slate-50")}>
    <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{titulo}</p>
    {!e ? (
      <p className="mt-1 text-xs text-slate-400">Sin datos para estimar</p>
    ) : e.error ? (
      <p className="mt-1 text-xs text-rose-600">{e.error}</p>
    ) : e.dias_descuento != null ? (
      <CeldaFactoraje e={e} />
    ) : (
      <>
        <p className="mt-0.5 text-sm font-semibold text-slate-800">
          {mxn(e.monto)} · {formatearPlazo(e.plazo, e.periodicidad)}
        </p>
        <p className="mt-1 text-lg font-semibold text-slate-900">
          {mxn(e.pago_periodo)}
          <span className="ml-1 text-[11px] font-normal text-slate-500">por periodo</span>
        </p>
        <dl className="mt-1 space-y-0.5 text-[11px] text-slate-600">
          <div className="flex justify-between"><dt>Pagos</dt><dd className="font-mono">{e.pagos}</dd></div>
          <div className="flex justify-between"><dt>Total a pagar</dt><dd className="font-mono">{mxn(e.total_pagar)}</dd></div>
          <div className="flex justify-between"><dt>Recibe</dt><dd className="font-mono">{mxn(e.neto_a_recibir)}</dd></div>
          <div className="flex justify-between"><dt>CAT estimado</dt><dd className="font-mono">{e.cat != null ? `${e.cat}%` : "—"}</dd></div>
        </dl>
      </>
    )}
  </div>
);

/** Vista previa SOLO LECTURA del pago por periodo: lo solicitado vs. lo que se autorizaría. */
export default function VistaPreviaComite({ solicitudId, montoAut, plazoAut }) {
  const [prev, setPrev] = useState(null);
  const [cargando, setCargando] = useState(false);

  useEffect(() => {
    if (!solicitudId) return;
    let vivo = true;
    const t = setTimeout(async () => {
      setCargando(true);
      try {
        const { data } = await api.post("/comite/vista-previa", {
          solicitud_id: solicitudId,
          monto_autorizado: Number(montoAut) || null,
          plazo_autorizado: Number(plazoAut) || null,
        });
        if (vivo) setPrev(data);
      } catch { if (vivo) setPrev(null); }
      finally { if (vivo) setCargando(false); }
    }, 450);
    return () => { vivo = false; clearTimeout(t); };
  }, [solicitudId, montoAut, plazoAut]);

  if (!prev?.solicitado && !prev?.autorizado) return null;
  return (
    <div className="mt-3 rounded-md border border-slate-200 bg-slate-50/60 p-2.5"
      data-testid="comite-vista-previa">
      <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
        <Calculator className="h-3.5 w-3.5" /> Vista previa del pago
        {cargando && <Loader2 className="h-3 w-3 animate-spin" />}
        <span className="ml-auto rounded-full border border-slate-300 bg-white px-1.5 py-0.5 text-[9px] font-semibold normal-case text-slate-500">
          Solo lectura · no guarda nada
        </span>
      </p>
      <div className="grid gap-2 sm:grid-cols-2">
        <Celda titulo="Con lo solicitado" e={prev.solicitado}
          testid="comite-previa-solicitado" />
        <Celda titulo="Con lo que autorizas" e={prev.autorizado} destacar
          testid="comite-previa-autorizado" />
      </div>
      <p className="mt-2 text-[10px] text-slate-500">
        Estimación con la tasa ({prev.tasa_ordinaria}%), la comisión y el IVA ya sellados —
        esos no cambian. La tabla definitiva la arma el sistema al contratar y al desembolsar,
        con las fechas reales y los días inhábiles.
      </p>
    </div>
  );
}
